/**
 * Pure helpers that turn a nested create form's cleaned data into a
 * DraftEntity, and into a seq-neutral synthetic CrmObject used to render
 * the draft chip.
 */

import type { DraftEntity } from '@/lib/crm/core/draftEntities';
import { buildOptimisticCrmObject } from '@/lib/crm/core/optimisticCrmObject';
import type { EntityTypeName } from '@/lib/crm/core/types';
import type { CrmObject, OverwriteTarget } from '@/types/crm';

export function buildDraftEntity(
  entityType: string,
  data: Record<string, unknown>
): DraftEntity {
  // CrmCreateForm sends { ...properties, associations?, draft_entities?,
  // overwrite_targets? } — same split as
  // AssociatedCrmObjectInput.handleCreateSubmit, PLUS an explicit strip of the
  // two control fields. `draft_entities` (a depth-2 nested create) and
  // `overwrite_targets` (an acknowledged association conflict) are batch/submit
  // control fields, not CRM properties; without pulling them out here they land
  // in `properties` and later ship as nonsensical CRM properties on this
  // draft's objectCreated event, which the backend rejects.
  //
  // `overwrite_targets` must NOT be dropped, however: when a draft association
  // targets an existing single-cardinality record the nested form can capture
  // the user's overwrite acknowledgement, and overwrite authorization is
  // batch-scoped (see EventBatch.overwrite_targets). We carry it on the draft
  // (out of `properties`, so it can never malform the objectCreated event) and
  // the parent form merges it into the submission's top-level overwrite_targets
  // — otherwise the folded associationAdded would be submitted unauthorized and
  // the backend would reject the whole batch with a cardinality violation.
  //
  // Depth-2 draft *capture* is out of scope (the sibling nested-form UX issue
  // suppresses it via hideCreateOption).
  const {
    associations,
    draft_entities: _draftEntities,
    overwrite_targets,
    ...properties
  } = data;
  const entity_id = crypto.randomUUID();
  const overwriteTargets = overwrite_targets as OverwriteTarget[] | undefined;
  return {
    entity_id,
    entity_type_name: entityType,
    properties,
    associations:
      (associations as Array<{
        association_type_name: string;
        target_entity_id: string;
      }>) ?? [],
    ...(overwriteTargets && overwriteTargets.length > 0
      ? { overwrite_targets: overwriteTargets }
      : {}),
  };
}

/**
 * Synthetic CrmObject used to render a draft chip. Delegates to the shared
 * optimistic-object builder so draft chips and eager nested creates always
 * share one shape — including the seq-neutral `last_event_seq: 0` that keeps
 * the store's seq guard from skipping the real objectCreated at submit (KD3/KD8).
 */
export function buildDraftSummaryObject(
  draft: DraftEntity,
  orgId: number
): CrmObject {
  return buildOptimisticCrmObject({
    entityId: draft.entity_id,
    entityType: draft.entity_type_name as EntityTypeName,
    orgId,
    properties: draft.properties,
    associations: draft.associations,
  });
}
